import { useState, useMemo, useEffect } from "react";
import { 
  CheckCircle2, 
  AlertCircle, 
  XCircle, 
  ChevronDown, 
  ChevronUp,
  Info,
  Link,
  Table as TableIcon,
  List,
  Heading2,
  FileSearch,
  Hash,
  MessageSquareQuote
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface SEOAnalysisProps {
  content: string;
  palavraChave: string;
  palavrasSecundarias: string[];
  metaTitle: string;
  metaDescription: string;
  slug: string;
  imagemCapa: string | null;
  faq: any;
  onScoreChange?: (score: number) => void;
}

export function SEOAnalysisPanel({
  content,
  palavraChave,
  palavrasSecundarias,
  metaTitle,
  metaDescription,
  slug,
  imagemCapa,
  faq,
  onScoreChange
}: SEOAnalysisProps) {
  const [isOpen, setIsOpen] = useState(true);

  const analysis = useMemo(() => {
    if (!content) return { items: [], score: 0 };

    const kw = palavraChave?.toLowerCase().trim();
    const text = content.toLowerCase();
    const plainText = content.replace(/[#*`_~]/g, "").toLowerCase();
    const wordCount = plainText.split(/\s+/).filter(w => w.length > 0).length;
    
    const items = [
      // Palavra-chave principal
      {
        id: "kw_title",
        label: "Palavra-chave no título",
        status: metaTitle?.toLowerCase().includes(kw) ? "success" : "error",
        explanation: "Ter a palavra-chave no título ajuda o Google a entender do que se trata o artigo."
      },
      {
        id: "kw_intro",
        label: "Aparece nos primeiros 100 caracteres",
        status: plainText.substring(0, 300).includes(kw) ? "success" : "error",
        explanation: "Indica relevância imediata para o leitor e motores de busca."
      },
      {
        id: "kw_h2",
        label: "Aparece em pelo menos um H2",
        status: content.match(new RegExp(`^##.*${kw}`, "im")) ? "success" : "warning",
        explanation: "Reforça o tema principal em sub-tópicos."
      },
      {
        id: "kw_meta",
        label: "Aparece na meta description",
        status: metaDescription?.toLowerCase().includes(kw) ? "success" : "warning",
        explanation: "Aumenta o CTR (taxa de clique) quando o usuário vê o termo destacado no Google."
      },
      {
        id: "kw_slug",
        label: "Aparece na URL (slug)",
        status: slug?.toLowerCase().includes(kw?.replace(/\s+/g, "-")) ? "success" : "error",
        explanation: "URLs amigáveis com a palavra-chave são um fator direto de ranqueamento."
      },
      {
        id: "kw_density",
        label: "Densidade da palavra-chave",
        status: (() => {
          if (!kw || wordCount === 0) return "error";
          const occurrences = (text.match(new RegExp(kw, "gi")) || []).length;
          const density = (occurrences / wordCount) * 100;
          if (density > 3) return "warning";
          if (density >= 0.5) return "success";
          return "error";
        })(),
        value: (() => {
          if (!kw || wordCount === 0) return "0%";
          const occurrences = (text.match(new RegExp(kw, "gi")) || []).length;
          const density = (occurrences / wordCount) * 100;
          const safeDensity = isFinite(density) ? density : 0;
          return `${safeDensity.toFixed(1)}% (${occurrences}x)`;
        })(),
        explanation: "O ideal é entre 0,5% e 2,5%. Acima de 3% pode ser considerado 'keyword stuffing'."
      },
      // Estrutura
      {
        id: "word_count",
        label: "Contagem de palavras",
        status: wordCount >= 800 ? "success" : wordCount >= 500 ? "warning" : "error",
        value: `${wordCount} palavras`,
        explanation: "Artigos mais longos (800+) costumam ter melhor performance em termos de profundidade."
      },
      {
        id: "h2_count",
        label: "Pelo menos 3 subtítulos H2",
        status: (content.match(/^##\s/gm) || []).length >= 3 ? "success" : "error",
        explanation: "Subtítulos melhoram a legibilidade e organização do conteúdo."
      },
      {
        id: "heading_order",
        label: "Ordem hierárquica de headings",
        status: (() => {
          const headings = content.match(/^#{1,6}\s/gm) || [];
          let currentLevel = 1;
          for (const h of headings) {
            const level = h.trim().length;
            if (level > currentLevel + 1) return "error";
            currentLevel = level;
          }
          return "success";
        })(),
        explanation: "Evitar saltos (ex: H2 para H4) ajuda robôs e leitores de tela."
      },
      {
        id: "has_faq",
        label: "Possui FAQ",
        status: faq && Array.isArray(faq) && faq.length > 0 ? "success" : "warning",
        explanation: "Perguntas frequentes ajudam a capturar rich snippets no Google."
      },
      {
        id: "has_list_table",
        label: "Possui tabela ou lista",
        status: (content.match(/^\s*[-*]\s/m) || content.match(/\|.*\|/m)) ? "success" : "warning",
        explanation: "Elementos visuais diferentes quebram a monotonia do texto."
      },
      {
        id: "cover_alt",
        label: "Capa com texto alternativo",
        status: imagemCapa ? "success" : "error", // Simplicando para este MVP, mas ideally checking alt
        explanation: "Imagens com alt text são essenciais para acessibilidade e SEO de imagens."
      },
      {
        id: "internal_links",
        label: "Links internos",
        status: (content.match(/\[.*\]\(\/guias\//g) || []).length >= 2 ? "success" : "warning",
        explanation: "Pelo menos 2 links para outros artigos do blog."
      }
    ];

    // Palavras secundárias
    if (palavrasSecundarias.length > 0) {
      const found = palavrasSecundarias.filter(s => text.includes(s.toLowerCase().trim()));
      items.push({
        id: "secondary_kw",
        label: "Palavras-chave secundárias",
        status: found.length === palavrasSecundarias.length ? "success" : found.length > 0 ? "warning" : "error",
        value: `${found.length}/${palavrasSecundarias.length}`,
        explanation: `Encontradas: ${found.join(", ") || "nenhuma"}`
      });
    }

    const successCount = items.filter(i => i.status === "success").length;
    const warningCount = items.filter(i => i.status === "warning").length;
    const score = Math.round(((successCount + warningCount * 0.5) / items.length) * 100);

    return { items, score };
  }, [content, palavraChave, palavrasSecundarias, metaTitle, metaDescription, slug, imagemCapa, faq]);

  useEffect(() => {
    onScoreChange?.(analysis.score);
  }, [analysis.score, onScoreChange]);

  const getStatusIcon = (status: string) => {
    if (status === "success") return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    if (status === "warning") return <AlertCircle className="w-4 h-4 text-amber-500" />;
    return <XCircle className="w-4 h-4 text-red-500" />;
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "bg-green-500";
    if (score >= 60) return "bg-amber-500";
    return "bg-red-500";
  };

  const getScoreTextColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-amber-600";
    return "text-red-600";
  };

  return (
    <div className="border rounded-xl overflow-hidden bg-white shadow-sm">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-4 flex-1">
          <FileSearch className="w-5 h-5 text-gray-500" />
          <div className="flex-1 text-left">
            <span className="font-bold text-[#0C1A12]">Análise de SEO</span>
            <div className="flex items-center gap-2 mt-1">
              <Progress value={analysis.score} className="h-1.5 w-32" />
              <span className={cn("text-xs font-bold", getScoreTextColor(analysis.score))}>
                {analysis.score}/100
              </span>
            </div>
          </div>
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {isOpen && (
        <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
          <TooltipProvider>
            {analysis.items.map((item) => (
              <div key={item.id} className="flex items-center justify-between group">
                <div className="flex items-center gap-2">
                  {getStatusIcon(item.status)}
                  <span className="text-sm text-gray-700">{item.label}</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <Info className="w-3 h-3 text-gray-400" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[200px]">
                      <p className="text-xs">{item.explanation}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                {item.value && (
                  <span className="text-[10px] font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-500">
                    {item.value}
                  </span>
                )}
              </div>
            ))}
          </TooltipProvider>
        </div>
      )}
    </div>
  );
}
